import Layer from "@arcgis/core/layers/Layer";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setEditableFeature,
  setEditableLayer,
  setEditorDrawActive,
  setEditorUpdateType,
} from "../../../redux/action/Dashboard-action";
import {
  asOfDateFieldName,
  currAsOfDateFieldName,
  getLayerFromId,
  graphicsLayer,
  view,
} from "../../../utils/API";
import { ConfigContext } from "../../../utils/ConfigContext";
import { getFieldsByPattern, getLayerReports } from "../../../utils/helper";
import { getLayerSymbology } from "../../../utils/symbologies";
import {
  ALL_STEPS,
  EditType,
  LAYER_EFFECT,
  pointSymbol,
} from "./EditorSwiper/EditorSwiper";
import { addLayerEffect, getFeatureNameField } from "./helpers";

export const baseLineColor = "#34495e";

export const INITIAL_CLICKED_POINTS = [
  {
    id: `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    graphic: undefined,
    coordinate: [],
    label: "",
    searchable: true,
  },
  {
    id: `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    graphic: undefined,
    coordinate: [],
    label: "",
    searchable: true,
  },
];

export const UpdateType = Object.freeze({
  situational: "situational",
  baseline: "baseline",
});

export const UPDATE_STATUS_TYPE = Object.freeze({
  UPDATE: "update",
  CURRENT: "current",
});

const publicFieldsCache = {};

export const EditorContext = createContext(null);

const EditorContextProvider = ({ children, editType, ...props }) => {
  const { config } = useContext(ConfigContext);

  const [showWarning, setShowWarning] = useState({
    show: false,
    action: null,
  });

  const [visibleEditableLayers, setVisibleEditableLayers] = useState();
  const [step, setStep] = useState("selectLayer");
  const [editableLayers, setEditableLayers] = useState([]);
  const [airdromeType, setAirdromeType] = useState(null);
  const [addedPoints, setAddedPoints] = useState([]);
  const [highlightHandle, setHighlightHandle] = useState(null);
  const [highlightSymbol, setHighlightSymbol] = useState(null);

  const reduxState = useSelector((state) => state.dashboard);

  const editableFeature = props?.editableFeature ?? reduxState.editableFeature;
  const editableLayer = props?.editableLayer ?? reduxState.editableLayer;
  const editorDrawActive =
    props?.editorDrawActive ?? reduxState.editorDrawActive;
  const editUpdateType = props?.editUpdateType ?? reduxState.editUpdateType;

  const [attachments, setAttachments] = useState({
    previews: {},
    files: [],
  });
  const [prefilledFields, setPrefilledFields] = useState([]);
  const [sketchGraphicsLayer, setSketchGraphicsLayer] = useState(null);
  const [sketchViewModel, setSketchViewModel] = useState(null);
  const [popupFeature, setPopupFeature] = useState(false);
  const [editableLayerLoaded, setEditableLayerLoaded] = useState(false);

  //for adding point in roadsSituational layer
  const [clickedPoints, setClickedPoints] = useState(INITIAL_CLICKED_POINTS);
  const [trimmedSituationalFeatures, setTrimmedSituationalFeatures] = useState({
    updateFeatures: [],
    deleteFeatures: [],
  });

  //for batch update
  const [situationalUpdateType, setSituationalUpdateType] = useState(
    UPDATE_STATUS_TYPE.UPDATE
  );
  const [batchUpdateFeatures, setBatchUpdateFeatures] = useState([]);
  const [editableFeatureList, setEditableFeatureList] = useState([]);
  const [editorListRefreshCount, setEditorListRefreshCount] = useState(0);
  const [editingInitialUpdateType, setEditingInitialUpdateType] =
    useState(null);
  const [publicFields, setPublicFields] = useState([]);

  const sketchGraphicsLayerRef = useRef(null);

  const reportsLayerMap = useSelector(
    (state) => state.dashboard.reportsLayerMap
  );

  const filters = useSelector((state) => state.filters);
  const activeModule = useSelector((state) => state.activeModule);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!editableLayer) {
      setPublicFields([]);
      return;
    }

    const layerId = editableLayer?.layerConfig?.viewer;
    const instance = editableLayer?.layerConfig?.instance;
    if (!layerId) return;

    // Check cache first
    if (publicFieldsCache[layerId]) {
      setPublicFields(publicFieldsCache[layerId]);
      return;
    }

    let layerPromise = getLayerFromId(Layer, layerId, instance);
    layerPromise.then((layer) => {
      layer
        .load()
        .then((l) => {
          const fields = l.fields.map((f) => f.name);
          fields.push("CreationDate");
          publicFieldsCache[layerId] = fields; // Cache it
          setPublicFields(fields);
        })
        .catch((err) => {
          console.log(err);
        });
    });
  }, [editableLayer]);

  const setHighlightFeature = useCallback((feature) => {
    if (props.setEditableFeature) {
      props.setEditableFeature(feature);
    } else {
      dispatch(setEditableFeature(feature));
    }
  }, []);

  const handleSetEditableLayer = useCallback((layer) => {
    if (props.setEditableLayer) {
      props.setEditableLayer(layer);
    } else {
      dispatch(setEditableLayer(layer));
    }
  }, []);

  useEffect(() => {
    sketchGraphicsLayerRef.current = sketchGraphicsLayer;
  }, [sketchGraphicsLayer]);

  const resetClickedPoints = useCallback(() => {
    setClickedPoints(INITIAL_CLICKED_POINTS);
  }, []);

  const setShowRoadsEditor = useCallback((value) => {
    if (props.setEditorDrawActive) {
      props.setEditorDrawActive(value);
    } else {
      dispatch(setEditorDrawActive(value));
    }
  }, []);

  const resetHighlightFeature = useCallback(() => {
    if (highlightSymbol) {
      const graphicToRemove = graphicsLayer.graphics.find(
        (g) => g.symbol.id === highlightSymbol.id
      );
      graphicsLayer.remove(graphicToRemove);
    }
    setHighlightSymbol(null);
    setHighlightFeature(null);
    highlightHandle?.remove();
    setHighlightHandle(null);
  }, [highlightHandle, highlightSymbol]);

  const resetBatchUpdateFeatures = useCallback(() => {
    batchUpdateFeatures.forEach((item) => {
      graphicsLayer.remove(item.graphic);
    });

    setBatchUpdateFeatures([]);
  }, [batchUpdateFeatures]);

  const resetEditor = () => {
    setStep(EditType.edit + "-" + ALL_STEPS.selectLayer);
    resetBatchUpdateFeatures();
    setAirdromeType(null);
    setAttachments({
      previews: {},
      files: [],
    });
    setPrefilledFields([]);

    graphicsLayer.removeAll();
    view.graphics.removeAll();
    if (sketchGraphicsLayerRef.current) {
      sketchGraphicsLayerRef.current?.removeAll();
      view.map.remove(sketchGraphicsLayerRef.current);
    }

    setSketchGraphicsLayer(null);
    setSketchViewModel(null);
    setPopupFeature(false);
    resetClickedPoints();
    setShowRoadsEditor(false);
    handleSetEditableLayer(null);
    setHighlightFeature(null);
    setEditableFeatureList([]);
    setEditableLayerLoaded(false);
  };

  const requiredFields = useMemo(() => {
    if (!editableLayer) return [];

    const layerRequiredFields =
      editableLayer?.layerConfig?.requiredFields || [];

    const rFields = [...layerRequiredFields];
    const requiredDateField =
      editUpdateType === UpdateType.baseline
        ? asOfDateFieldName
        : currAsOfDateFieldName;

    const hasDateFields =
      editableLayer.fields.some((field) => field.name === requiredDateField)
        .length > 0;

    if (!rFields.includes(requiredDateField) || hasDateFields) {
      rFields.push(requiredDateField);
    }
    return [...rFields];
  }, [editableLayer, config, editUpdateType]);

  const getFields = useCallback((fields, requiredFields, filteredFields) => {
    return fields.filter(
      (f) =>
        f.editable &&
        requiredFields.includes(f.name) &&
        !filteredFields.includes(f.name)
    );
  }, []);

  const baseLineRequiredFields = useMemo(() => {
    if (!editableLayer && !editableLayer?.layerConfig) return [];

    const nameField = getFeatureNameField(editableLayer);
    const filteredFields = [
      nameField,
      ...prefilledFields.map((item) => item.fieldName),
    ];
    const symbology = getLayerSymbology(editableLayer, config) || {};
    if (symbology.iconMap?.fields) {
      const iconFields = editableLayer.fields
        .filter((field) => symbology.iconMap.fields.includes(field.name))
        .map((f) => f.name);
      filteredFields.push(...iconFields);
    }

    if (!editableLayer.layerConfig?.baselineFields) return [];

    const baselineFields = getFields(
      getFieldsByPattern(
        editableLayer,
        editableLayer.layerConfig.baselineFields
      ),
      requiredFields,
      filteredFields
    );

    return baselineFields;
  }, [editableLayer, requiredFields, config]);

  const activeColor = useMemo(
    () =>
      editUpdateType === UpdateType.baseline ? baseLineColor : config.opsColor,
    [editUpdateType, config, editType]
  );

  const handleSetUpdateType = useCallback((type) => {
    if (props.setEditUpdateType) {
      props.setEditUpdateType(type);
    } else {
      dispatch(setEditorUpdateType(type));
    }
  }, []);

  const addGraphic = useCallback(
    (editingGraphic, editingSymbol, callback, skipAdding = false) => {
      const nGraphic = editingGraphic?.clone();
      nGraphic.symbol = editingGraphic ? editingSymbol : pointSymbol;

      if (editingGraphic && nGraphic.symbol.type === "picture-marker") {
        const width = nGraphic.symbol.width;
        const height = nGraphic.symbol.height;

        const symbol = new PictureMarkerSymbol({
          url: nGraphic.symbol?.url,
          width: nGraphic.symbol.width,
          height: nGraphic.symbol.height,
        });
        nGraphic.symbol = symbol;
        nGraphic.symbol.width = width >= 50 ? width / 2 : width;
        nGraphic.symbol.height = height >= 50 ? height / 2 : height;
      }

      setHighlightFeature(nGraphic);

      if (!skipAdding) {
        graphicsLayer.add(nGraphic);
      }

      if (callback) {
        callback();
      }
    },
    [setHighlightFeature]
  );

  const handleSelectFeature = async (clickedLayer, feature, callback) => {
    try {
      highlightHandle?.remove();

      const query = clickedLayer.createQuery();
      const objectIdField = clickedLayer.objectIdField;
      query.objectIds = [feature.attributes[objectIdField]];
      query.outFields = ["*"];
      query.returnGeometry = true;
      let editingGraphic = feature;

      let editingSymbol;
      clickedLayer.effect = LAYER_EFFECT;
      addLayerEffect([]);

      if (
        clickedLayer.layerConfig.isConops ||
        clickedLayer.layerConfig.isEpam ||
        clickedLayer.layerConfig.isEditorCurved ||
        clickedLayer.layerConfig.isShownInReportManager
      ) {
        const result = await clickedLayer.queryFeatures(query);
        const graphic = result.features.find(
          (feat) => feat[objectIdField] === editingGraphic[objectIdField]
        );
        if (
          clickedLayer.layerConfig.isConops ||
          clickedLayer.layerConfig.isEpam
        ) {
          editingSymbol = pointSymbol;
          setHighlightSymbol(pointSymbol);
          if (!!graphic) {
            graphic.symbol = pointSymbol;
          }
        } else {
          const symbol = await clickedLayer.renderer?.getSymbolAsync(
            editingGraphic
          );
          editingSymbol = symbol;

          setHighlightSymbol(symbol);
        }

        if (!!graphic) {
          editingGraphic = graphic;
        }
      } else {
        const layer = editingGraphic?.sourceLayer
          ? editingGraphic?.sourceLayer
          : editingGraphic?.layer;

        if (layer) {
          const symbol = await layer.renderer?.getSymbolAsync(editingGraphic);
          editingSymbol = symbol;

          setHighlightSymbol(symbol);
        }
      }

      addGraphic(
        editingGraphic,
        editingSymbol,
        callback,
        clickedLayer?.layerConfig?.titleLabel === "roads"
      );

      return editingGraphic;
    } catch (err) {
      console.log(err);
    }
  };

  const hasEditedAttachments = useMemo(() => {
    if (!Array.isArray(attachments?.files)) return;

    const hasEditedExisting = attachments.files.some(
      (item) => item?.attachmentId && item?.isDeleted
    );

    const hasAddedNew = attachments.files.some(
      (item) => item instanceof File && !item?.isDeleted
    );

    return hasEditedExisting || hasAddedNew;
  }, [attachments]);

  const queryFields = useMemo(() => {
    if (!editableLayer?.layerConfig || !editableLayer || !editType) return [];

    let fields = [editableLayer.objectIdField];
    if (Array.isArray(editableLayer.layerConfig.baselineFields)) {
      fields.push(...editableLayer.layerConfig.baselineFields);
    }

    if (Array.isArray(editableLayer.layerConfig.situationalFields)) {
      fields.push(...editableLayer.layerConfig.situationalFields);
    }

    if (Array.isArray(editableLayer.layerConfig.batchFields)) {
      fields.push(...editableLayer.layerConfig.batchFields);
    }

    const nameField = getFeatureNameField(editableLayer);
    if (nameField && !fields.includes(nameField)) {
      fields.push(nameField);
    }

    if (!fields.includes(currAsOfDateFieldName)) {
      fields.push(currAsOfDateFieldName);
    }

    if (!fields.includes(asOfDateFieldName)) {
      fields.push(asOfDateFieldName);
    }

    if (editableLayer.layerConfig.isShownInReportManager) {
      fields.push("reporttype");
    }

    if (editableLayer.editFieldsInfo?.editDateField) {
      fields.push(editableLayer.editFieldsInfo.editDateField);
    }

    const fieldsToQuery = editableLayer.fields.filter((field) =>
      fields.includes(field.name)
    );

    return fieldsToQuery.map((f) => f.name);
  }, [editUpdateType, editableLayer]);

  const layerReports = useMemo(() => {
    if (!editableLayer || Object.keys(reportsLayerMap).length === 0) return [];
    const reports = getLayerReports(editableLayer, reportsLayerMap);

    return reports;
  }, [editableLayer, reportsLayerMap]);

  const reportsFeatureMap = useMemo(() => {
    const filtersArray = filters[activeModule]?.currvalidationfield || [];
    const reportsByFeatureId = layerReports.reduce((acc, report) => {
      const featureId = report.attributes?.reportedfeatureid;
      const validationStatus = report.attributes?.currvalidationfield;
      const hasReportFilters = "currvalidationfield" in filters[activeModule];

      if (
        !featureId ||
        (hasReportFilters && !filtersArray.includes(validationStatus))
      )
        return acc;
      if (!acc[featureId]) {
        acc[featureId] = [];
      }

      acc[featureId].push(report);
      return acc;
    }, {});

    return reportsByFeatureId;
  }, [layerReports, filters, activeModule, reportsLayerMap]);

  const isReportsUpdate = useMemo(() => {
    let result = false;
    try {
      if (
        editableFeature &&
        editableFeature.attributes &&
        editableFeature.attributes.reporttype === 2
      ) {
        result = true;
      }
    } catch (err) {}

    return result;
  }, [editableFeature]);

  return (
    <EditorContext.Provider
      value={{
        editableLayer,
        visibleEditableLayers,
        setVisibleEditableLayers,
        step,
        editableLayers,
        setStep,
        setEditableLayers,
        airdromeType,
        setAirdromeType,
        addedPoints,
        setAddedPoints,
        highlightHandle,
        setHighlightHandle,
        highlightSymbol,
        setHighlightSymbol,
        highlightFeature: editableFeature,
        setHighlightFeature,
        attachments,
        setAttachments,
        prefilledFields,
        setPrefilledFields,
        sketchGraphicsLayer,
        setSketchGraphicsLayer,
        sketchViewModel,
        setSketchViewModel,
        setPopupFeature,
        popupFeature,
        showWarning,
        setShowWarning,
        clickedPoints,
        setClickedPoints,
        resetClickedPoints,
        resetHighlightFeature,
        showRoadsEditor: editorDrawActive,
        setShowRoadsEditor,
        setEditableLayer: handleSetEditableLayer,
        updateType: editUpdateType,
        setUpdateType: handleSetUpdateType,
        batchUpdateFeatures,
        setBatchUpdateFeatures,
        resetBatchUpdateFeatures,
        activeColor,
        resetEditor,
        requiredFields,
        baseLineRequiredFields,
        situationalUpdateType,
        setSituationalUpdateType,
        handleSelectFeature,
        editableFeatureList,
        setEditableFeatureList,
        editorListRefreshCount,
        setEditorListRefreshCount,
        editingInitialUpdateType,
        setEditingInitialUpdateType,
        editableLayerLoaded,
        setEditableLayerLoaded,
        editType,
        hasEditedAttachments,
        queryFields,
        setTrimmedSituationalFeatures,
        trimmedSituationalFeatures,
        layerReports,
        publicFields,
        isReportsUpdate,
        reportsFeatureMap,
      }}
    >
      {children}
    </EditorContext.Provider>
  );
};

export default EditorContextProvider;
